import {
  IconCalendar,
  IconClubs,
  IconPlus,
  IconSettings,
  IconUsers,
} from '@ui/icons'
import { EVENT_NAV, type NavItem } from '@ui/nav-config'
import { useEventMode } from '@ui/useEventMode'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { useCurrentAccount } from '@/contexts/CurrentAccountProvider'

type Props = {
  eventId: string | null
  onNewEvent?: () => void
}

type Slot = { kind: 'tab'; tab: NavItem } | { kind: 'fab' }

const TabLink = ({ tab }: { tab: NavItem }) => {
  const { t } = useTranslation()
  return (
    <NavLink
      to={tab.to}
      end={tab.exact}
      className={({ isActive }) =>
        // Use text-white/55 not text-green-100/65: Tailwind's /opacity modifier
        // drops alpha on var(--token) colors, so SVG currentColor went black.
        `relative flex flex-col items-center justify-center gap-1 py-2 text-[11px] font-semibold transition-colors ${
          isActive ? 'text-white' : 'text-white/55 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden="true"
              className="absolute -top-px w-6 h-[3px] rounded-sm bg-flag"
            />
          )}
          <span className="grid place-items-center w-[22px] h-[22px]">
            <tab.Icon size={20} />
          </span>
          <span>{t(tab.shortLabelKey)}</span>
        </>
      )}
    </NavLink>
  )
}

const Fab = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="relative flex flex-col items-center gap-1 py-1 text-[11px] font-semibold text-green-100/85"
  >
    <span
      className="w-12 h-12 -mt-4 grid place-items-center rounded-full bg-flag text-white"
      style={{
        // 5px cream-bg ring punches an iOS-style notch in the dark glass where
        // the FAB sits inside the nav, and blends into the page above it.
        boxShadow:
          '0 0 0 5px var(--cream-bg), 0 6px 14px -4px rgba(14, 20, 16, 0.35)',
      }}
    >
      <IconPlus size={24} />
    </span>
    <span>{label}</span>
  </button>
)

const listModeSlots = (
  accountType: string | undefined,
  hasFab: boolean
): Slot[] => {
  const events: NavItem = {
    to: '/events',
    labelKey: 'nav.events',
    shortLabelKey: 'nav-short.events',
    Icon: IconCalendar,
  }
  const users: NavItem = {
    to: '/users',
    labelKey: 'nav.users',
    shortLabelKey: 'nav-short.users',
    Icon: IconUsers,
  }
  const slot4: NavItem =
    accountType === 'PLATFORM'
      ? {
          to: '/platform/courses',
          labelKey: 'nav.courses',
          shortLabelKey: 'nav-short.courses',
          Icon: IconClubs,
        }
      : {
          // TODO 2b: placeholder slot for a future tenant-relevant surface.
          to: '/users',
          labelKey: 'nav.users',
          shortLabelKey: 'nav-short.users',
          Icon: IconUsers,
        }
  const account: NavItem = {
    to: '/account-settings',
    labelKey: 'nav.account-settings',
    shortLabelKey: 'nav-short.account',
    Icon: IconSettings,
  }

  const center: Slot = hasFab ? { kind: 'fab' } : { kind: 'tab', tab: slot4 }
  return [
    { kind: 'tab', tab: events },
    { kind: 'tab', tab: users },
    center,
    { kind: 'tab', tab: slot4 },
    { kind: 'tab', tab: account },
  ]
}

const eventModeSlots = (eventId: string, mode: string | null): Slot[] =>
  EVENT_NAV(eventId, mode).map((tab) => ({ kind: 'tab', tab }))

const MobileBottomNav = ({ eventId, onNewEvent }: Props) => {
  const { t } = useTranslation()
  const currentAccount = useCurrentAccount()
  const eventMode = useEventMode(eventId)

  const slots: Slot[] = eventId
    ? eventModeSlots(eventId, eventMode)
    : listModeSlots(currentAccount?.type, Boolean(onNewEvent))

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-20 px-3 pointer-events-none"
      style={{
        // 28px min so it reads as floating, not docked; iOS safe-area wins for home indicator.
        paddingBottom: 'max(28px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        className="pointer-events-auto mx-auto max-w-[520px] rounded-2xl border border-white/10 grid grid-cols-5 overflow-visible"
        style={{
          background: 'rgba(15, 58, 38, 0.55)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          boxShadow:
            '0 1px 0 rgba(255,255,255,0.08) inset, 0 18px 40px -16px rgba(11, 42, 28, 0.45)',
        }}
      >
        {slots.map((slot) =>
          slot.kind === 'fab' ? (
            <Fab
              key="fab"
              onClick={onNewEvent as () => void}
              label={t('fab.new-event')}
            />
          ) : (
            <TabLink key={slot.tab.to} tab={slot.tab} />
          )
        )}
      </div>
    </nav>
  )
}

export default MobileBottomNav
